import { type RefObject, useCallback } from "react";
import type { MarketMarketIO } from "../generated/fresh-guard-api";
import { lsLoad } from "../tools/Storage.ts";
import StorageEnum from "../enums/Storage.enum.ts";
import type { WebSocketType } from "./WebSocketType.ts";
import { warn } from "../tools/helpers/ConsoleHelper.ts";
import { useLogging } from "../provider/logging/Logging.hook.ts";
import { v4 } from "uuid";

/**
 * useSendStatus
 *
 * New semantics:
 * - only sends a \"change signal\" (market ID) over the WebSocket
 * - NO market components in the payload anymore (data comes from the API)
 *
 * The marketComponents parameter is kept but intentionally unused to avoid
 * breaking the existing call site in EditMarketProvider.
 */
export default function (wsRef: RefObject<WebSocket | null>) {
  const { logError } = useLogging();

  return useCallback(
    async (_marketComponents: unknown[]) => {
      // keep parameter for existing call sites; explicitly mark as used
      void _marketComponents;
      const market: MarketMarketIO = lsLoad(StorageEnum.Market);

      if (market?.id) {
        const webSocketType: WebSocketType = {
          type: "broadcast",
          marketId: market.id,
          data: null, // pure signal message, no sensitive data
        };

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify(webSocketType));
        } else {
          logError(
            "web socket not ready. cannot send message yet",
            "useSendStatus",
            v4(),
          );
          warn("web socket not ready. cannot send message yet");
        }
      } else {
        logError("market id empty", "useSendStatus", v4());
        warn("market id empty");
      }
    },
    [wsRef, logError],
  );
}
